class CartManager {
  constructor() {
    this.TAX_VARIANT_ID = 32356196155479;
    this.TAX_FEE_TITLE = 'Tax';
    this.TAX_SKU = 'TAX';
    this.isUpdatingTax = false; // Guard flag to prevent concurrent executions
  }

  async updateCartTaxItems() {
    // Prevent multiple simultaneous executions (race condition guard)
    if (this.isUpdatingTax) {
      console.log('Tax update already in progress, skipping duplicate call');
      return;
    }

    this.isUpdatingTax = true;

    try {
      let cart = await this.getCartItems();
      let totalTaxAmount = 0;

      totalTaxAmount = this.calculateCartTaxAmount(cart.items);

      // Round the total tax amount to 2 decimal places
      totalTaxAmount = Math.round(totalTaxAmount * 100) / 100;
      let taxQuantity = Math.round(totalTaxAmount * 100); // Convert dollars to cents

      // Get ALL existing tax items
      let existingTaxItems = cart.items.filter(item => item.sku === this.TAX_SKU);

      // Remove ALL existing tax items first to guarantee a clean slate
      // This prevents duplicate tax line items from accumulating
      if (existingTaxItems.length > 0) {
        console.log(`Found ${existingTaxItems.length} existing tax item(s). Removing all before adding new tax.`);

        // Remove each tax item by its unique key (not variant ID!)
        for (let taxItem of existingTaxItems) {
          console.log(`Removing tax item with key: ${taxItem.key}, quantity: ${taxItem.quantity}`);
          await this.updateLineItem(taxItem.key, 0);
        }
      }

      // Add a single new tax item with the correct amount
      if (totalTaxAmount > 0) {
        console.log(`Adding new tax item: $${totalTaxAmount} (quantity: ${taxQuantity})`);
        await this.addItemsToCart([{
          quantity: taxQuantity,
          id: this.TAX_VARIANT_ID,
          properties: {
            _legacy_product_as_fee_title: this.TAX_FEE_TITLE,
            _taxAmount: totalTaxAmount
            // Do NOT add timestamp to avoid creating duplicate line items later
          }
        }]);
      } else {
        console.log('No tax to add (totalTaxAmount is 0)');
      }

    } catch (error) {
      console.error('Error in updateCartTaxItems:', error);
    } finally {
      // Always release the lock, even if there was an error
      this.isUpdatingTax = false;
    }
  }

  calculateTaxAmount(price, taxRates) {
    let totalTaxRate = taxRates.reduce((sum, tax) => sum + parseFloat(tax.rate), 0);
    return price * totalTaxRate; // Return exact value, don't round yet
  }

  calculateCartTaxAmount(items) {
    let totalTaxAmount = 0;
    const bundleGroups = {};

    for (let item of items) {
      if (item.sku === this.TAX_SKU) continue;

      const normalizedItem = this.normalizeCartItem(item);
      const bundleId = normalizedItem.properties?._bundleId;
      const isMergedBundle =
        normalizedItem.properties?._isMergedBundle === "true" && bundleId;

      if (isMergedBundle) {
        if (!bundleGroups[bundleId]) {
          bundleGroups[bundleId] = [];
        }
        bundleGroups[bundleId].push(normalizedItem);
        continue;
      }

      totalTaxAmount += this.calculateLineTaxAmount(normalizedItem);
    }

    Object.values(bundleGroups).forEach((bundleItems) => {
      const primaryItem = this.findPrimaryBundleItem(bundleItems);
      const taxFromProperty = this.getTaxAmountFromProperty(primaryItem);

      if (taxFromProperty !== null) {
        totalTaxAmount += taxFromProperty;
        return;
      }

      bundleItems.forEach((item) => {
        totalTaxAmount += this.calculateLineTaxAmount(item);
      });
    });

    return totalTaxAmount;
  }

  calculateLineTaxAmount(item) {
    if (!item.taxable) return 0;

    const taxFromProperty = this.getTaxAmountFromProperty(item);
    if (taxFromProperty !== null) return taxFromProperty;

    const taxRates = this.normalizeTaxRates(item.properties?._taxRates);
    if (!taxRates.length) return 0;

    const itemPrice = this.getTaxableLinePrice(item);
    if (!itemPrice) return 0;

    return this.calculateTaxAmount(itemPrice, taxRates);
  }

  getTaxAmountFromProperty(item) {
    if (!item || !item.properties) return null;

    let taxPerItemQuantity = this.parseIntegerValue(
      item.properties._taxPerItemQuantity,
    );

    if (taxPerItemQuantity === null) {
      taxPerItemQuantity = this.parseIntegerValue(
        item.properties._data?._taxPerItemQuantity,
      );
    }

    if (taxPerItemQuantity === null) return null;

    return (taxPerItemQuantity * item.quantity) / 100;
  }

  getTaxableLinePrice(item) {
    const quantity = this.parseIntegerValue(item.quantity) || 0;
    if (!quantity) return 0;

    const candidatePrices = [
      item.final_line_price,
      item.line_price,
      item.original_line_price,
    ];

    for (let cents of candidatePrices) {
      if (typeof cents === "number" && cents > 0) {
        return cents / 100;
      }
    }

    if (typeof item.price === "number" && item.price > 0) {
      return (item.price * quantity) / 100;
    }

    return 0;
  }

  normalizeCartItem(item) {
    const properties = item.properties || {};

    return {
      ...item,
      properties: {
        ...properties,
        _data: this.normalizePropertyValue(properties._data),
        _taxRates: this.normalizeTaxRates(properties._taxRates),
        _bundleComponents: this.normalizePropertyValue(
          properties._bundleComponents,
        ),
      },
    };
  }

  normalizeTaxRates(value) {
    const normalized = this.normalizePropertyValue(value);

    if (!Array.isArray(normalized)) return [];

    return normalized.filter(
      (taxRate) =>
        taxRate &&
        typeof taxRate === "object" &&
        taxRate.rate !== undefined &&
        taxRate.rate !== null,
    );
  }

  normalizePropertyValue(value) {
    if (value === null || value === undefined) return value;
    if (Array.isArray(value)) return value;
    if (typeof value === "object") return value;
    if (typeof value !== "string") return value;

    const decodedValue = this.decodeHtmlEntities(value);

    if (
      window.CartDataEncoder &&
      typeof window.CartDataEncoder.decode === "function"
    ) {
      const cartDecoded = window.CartDataEncoder.decode(decodedValue);
      if (
        Array.isArray(cartDecoded) ||
        (cartDecoded && typeof cartDecoded === "object")
      ) {
        return cartDecoded;
      }
    }

    try {
      return JSON.parse(decodedValue);
    } catch (error) {
      return this.parseRubyHashFallback(decodedValue);
    }
  }

  parseRubyHashFallback(value) {
    if (!value || typeof value !== "string") return value;
    if (!value.includes("=>")) return value;

    if (value.trim().startsWith("[")) {
      return this.parseRubyHashArrayFallback(value);
    }

    const parsed = {};
    const pattern = /"([^"\\]+)"\s*=>\s*"((?:\\.|[^"\\])*)"/g;
    let match;

    while ((match = pattern.exec(value)) !== null) {
      parsed[match[1]] = match[2].replace(/\\"/g, '"');
    }

    return Object.keys(parsed).length ? parsed : value;
  }

  parseRubyHashArrayFallback(value) {
    const unwrapped = value.trim().replace(/^\[/, "").replace(/\]$/, "");
    if (!unwrapped) return [];

    return unwrapped
      .split(/\},\s*\{/)
      .map((item) => item.replace(/^\{/, "").replace(/\}$/, ""))
      .map((item) => {
        const parsed = {};
        const pattern = /"([^"\\]+)"\s*=>\s*"((?:\\.|[^"\\])*)"/g;
        let match;

        while ((match = pattern.exec(item)) !== null) {
          parsed[match[1]] = match[2].replace(/\\"/g, '"');
        }

        return parsed;
      })
      .filter((item) => Object.keys(item).length > 0);
  }

  decodeHtmlEntities(value) {
    return value
      .replace(/&quot;/g, '"')
      .replace(/&#34;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&gt;/g, ">")
      .replace(/&lt;/g, "<")
      .replace(/&amp;/g, "&");
  }

  parseIntegerValue(value) {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  findPrimaryBundleItem(bundleItems) {
    if (!bundleItems.length) return null;

    for (let item of bundleItems) {
      const properties = item.properties || {};
      const primaryVariantId = properties._primaryVariantId;
      const primarySku = properties._primarySku;

      if (primaryVariantId && item.variant_id) {
        if (
          primaryVariantId ===
          `gid://shopify/ProductVariant/${item.variant_id}`
        ) {
          return item;
        }
      }

      if (primarySku && item.sku === primarySku) {
        return item;
      }
    }

    for (let item of bundleItems) {
      if (item.properties?._primaryItem === "true") {
        return item;
      }
    }

    return bundleItems[0];
  }

  async updateCart(updates) {
    for (let [key, update] of Object.entries(updates)) {
      if (typeof update === 'number') {
        await this.updateLineItem(key, update);
      } else {
        let { quantity, properties } = update;
        await this.updateLineItem(key, quantity, properties);
      }
    }
  }

  async updateLineItem(itemKey, quantity, properties = null) {
    let updateData = {
      id: itemKey,
      quantity: quantity
    };

    if (properties || quantity > 0) {
      updateData.properties = {
        ...properties,
        _timestamp: Date.now() // Add timestamp to properties
      };
    }

    let response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      let errorText = await response.text();
      throw new Error(`Failed to update line item: ${response.status} ${response.statusText}\n${errorText}`);
    }
  }

  async addItemsToCart(items) {
    try {
      // Add timestamp to each item's properties (except tax items)
      // Tax items must have consistent properties to prevent duplicate line items
      const itemsWithTimestamp = items.map(item => {
        if (item.id === this.TAX_VARIANT_ID) {
          // Don't modify tax item properties - keep them static
          return item;
        }
        // Add timestamp to other items
        return {
          ...item,
          properties: {
            ...item.properties,
            _timestamp: Date.now()
          }
        };
      });

      let response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsWithTimestamp })
      });

      if (!response.ok) {
        let errorText = await response.text();
        throw new Error(`Failed to add items to cart: ${response.status} ${response.statusText}\n${errorText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error adding items to cart:', error);
      throw error;
    }
  }

  async getCartItems() {
    let response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error('Failed to fetch cart items');
    }
    return response.json();
  }
}
